import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  styled,
  Stack,
} from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { PrimaryButton } from '~/modules/onboarding/components/PrimaryButton';

interface WelcomeModalProps {
  open: boolean;
  treeOwnerName: string;
  treeOwnerProfileImage: string | null;
  onContinue: () => void;
  onClose: () => void;
}

const WelcomeModal = ({
  open,
  treeOwnerName,
  treeOwnerProfileImage,
  onContinue,
  onClose,
}: WelcomeModalProps) => {
  return (
    <StyledDialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      aria-labelledby="welcome-modal-title"
    >
      <DialogContentContainer>
        <ProfileImageContainer>
          {treeOwnerProfileImage ? (
            <ProfileImage src={treeOwnerProfileImage} alt={treeOwnerName} />
          ) : (
            <ProfileImagePlaceholder direction="row">
              {treeOwnerName.charAt(0).toUpperCase()}
            </ProfileImagePlaceholder>
          )}
        </ProfileImageContainer>

        <Box id="welcome-modal-title">
          <WelcomeText>Welcome to</WelcomeText>
          <TreeOwnerText>
            <strong>{treeOwnerName}'s</strong> Family Tree!
          </TreeOwnerText>
        </Box>

        <ButtonContainer>
          <PrimaryButton
            variant="green"
            onClick={onContinue}
            data-testid="welcome-modal-continue"
          >
            Continue
          </PrimaryButton>
        </ButtonContainer>
      </DialogContentContainer>
    </StyledDialog>
  );
};

export default WelcomeModal;

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    maxWidth: '400px',
    margin: theme.spacing(2),
  },
});

const DialogContentContainer = styled(DialogContent)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(5, 4, 4, 4),
  textAlign: 'center',
});

const ProfileImageContainer = styled(Box)({
  marginBottom: theme.spacing(3),
});

const ProfileImage = styled('img')({
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: `3px solid ${theme.palette.custom.beige}`,
});

const ProfileImagePlaceholder = styled(Stack)({
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  backgroundColor: theme.palette.custom.beige,
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '40px',
  fontWeight: 600,
  color: theme.palette.custom.darkBrown,
  border: `3px solid ${theme.palette.custom.beige}`,
});

const WelcomeText = styled(Typography)({
  fontSize: '24px',
  fontWeight: 400,
  color: theme.palette.custom.textTitle,
  marginBottom: theme.spacing(0.5),
});

const TreeOwnerText = styled(Typography)({
  fontSize: '24px',
  fontWeight: 400,
  color: theme.palette.custom.textTitle,
  marginBottom: theme.spacing(4),
  '& strong': {
    fontWeight: 600,
  },
});

const ButtonContainer = styled(Box)({
  width: '100%',
});
